import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ChatWelcome from './ChatWelcome';
import NewQuestionContainer from './NewQuestionContainer/NewQuestionContainer';
import NewChatbotCompletedScreen from './NewChatbotCompletedScreen';
import VoiceOnOffBtn from './VoiceOnOffBtn';
import AppContext from '../../../contexts/AppContext';
import GLOBAL from '../../../global';
import CONSTANT from '../../../constants/Constant';
import routes from '../../../constants/Routes';
import { playAudio, getMarkup } from '../../../utils/Utils';
import NewProgressbar from "../NewProgressBar";
import { createBotOptionFactory, mskOptions } from './business/createBotInfoObj';
import AlertModal from '../AlertModal';
import { useDispatch } from 'react-redux';
import { setShowBiometricCautionAlert, setBlocked, setQuestionAnswerInNew, advanceToNextQuestion } from '../../../features/chatbotNew/chatbotNewSlice';
import ExamService from '../../../services/ExamService';
import { saveSingleQuestionAnswer } from '../../editAssessmentNew/EditAssessmentService';
import { useTenantFeatures } from '../../../features/tenantFeatures/useTenantFeature';

const NewChatbotContainer = () => {
    const chatBotNewState = useSelector(state => state.chatBotNew);
    const { phase, testId, flatQuestions, answeredQuestions, currentQuestionIndex, showBiometricPrompt, showBiometricCautionAlert } = chatBotNewState;
    const dispatch = useDispatch();
    const showWelcomePage = useSelector(state => state.chatBot?.chatBotUIState?.showWelcomePage ?? true);
    const { isAudioPlayEnabled, audioPlayEnabled } = useContext(AppContext);

    // Sync audio state when new chatbot becomes active
    useEffect(() => {
        if (phase === 'active' && audioPlayEnabled) {
            // Ensure AppContext state matches GLOBAL state
            // This fixes the issue where old chatbot turned off audio
            if (isAudioPlayEnabled !== GLOBAL.AUDIO_PLAY_ENABLED) {
                audioPlayEnabled(GLOBAL.AUDIO_PLAY_ENABLED);
            }
        }
    }, [phase, isAudioPlayEnabled, audioPlayEnabled]);

    // Fresh Fix: Stop all audio when transitioning to completed phase for Examinetics skip
    useEffect(() => {
        if (phase === 'completed') {
            const invitationType = (chatBotNewState.invitationTypeName || '').toLowerCase();
            const isSelfAssessment = invitationType !== CONSTANT.REDIRECT_TYPE.INCLINIC && invitationType !== CONSTANT.REDIRECT_TYPE.TELEMEDICINE;

            if (isExamineticsEnabled && isSelfAssessment) {
                playAudio('remove');
            }
        }
    }, [phase, chatBotNewState.invitationTypeName, isExamineticsEnabled]);

    const mskBotInfo = createBotOptionFactory(mskOptions).botInfo;
    const botSubName = mskBotInfo?.subName || CONSTANT.MSK_BOT_INFO.subNames.default;

    const visibleCount = (flatQuestions || []).filter(q => !q.isDefaultHide).length;
    const progress = visibleCount > 0 ? Math.round(((answeredQuestions || []).length / visibleCount) * 100) : 0;

    const getBodyRegionFromAssessment = () => {
        const bodyRegionList = GLOBAL.ASSESSMENT?.BodyRegionList;
        if (!bodyRegionList || bodyRegionList.length === 0 || bodyRegionList[0] === 0) return null;

        return bodyRegionList.map(id => {
            const region = Object.values(CONSTANT.BODY_REGIONS).find(r => r.key === id);
            return region ? region.name : id;
        }).join(', ');
    };

    const onChangeAudioPlay = async () => {
        await playAudio('remove');
        const wasAudioEnabled = GLOBAL.AUDIO_PLAY_ENABLED;
        GLOBAL.AUDIO_PLAY_ENABLED = !GLOBAL.AUDIO_PLAY_ENABLED;
        if (audioPlayEnabled) {
            audioPlayEnabled(!isAudioPlayEnabled);
        }

        // If audio was just turned ON (was false, now true), play current question immediately
        if (!wasAudioEnabled && GLOBAL.AUDIO_PLAY_ENABLED) {
            const currentQuestion = flatQuestions?.[currentQuestionIndex];
            if (currentQuestion?.voiceUrl) {
                // Play question voice
                await playAudio(currentQuestion.voiceUrl, { priorityLevel: 3, ignoreInterval: true });

                // Wait 500ms then play response voices
                setTimeout(async () => {
                    if (currentQuestion.responses) {
                        for (const response of currentQuestion.responses) {
                            if (response?.voiceUrl) {
                                await playAudio(response.voiceUrl, { queue: true, priorityLevel: 3 });
                            }
                        }
                    }
                }, 500);
            }
        }
    };

    const { isExamineticsEnabled } = useTenantFeatures();
 
    const handleClickYesAtPopUpWarning = async () => {
        dispatch(setShowBiometricCautionAlert(false));
        dispatch(setBlocked("You should not proceed with this test. Please contact your administrator."));
    };

    const handleClickNoAtPopUpWarning = async () => {
        dispatch(setShowBiometricCautionAlert(false));
        const currentQuestion = flatQuestions[currentQuestionIndex];
        if (currentQuestion) {
            // Find the "No" response
            const noResponse = (currentQuestion.responses || []).find(r => (r.name || '').toLowerCase() === 'no');
            if (noResponse) {
                // Change answer to "No"
                dispatch(setQuestionAnswerInNew({
                    questionId: currentQuestion.questionId,
                    answerId: noResponse.id,
                    answerValue: noResponse.name
                }));
                // Save to API
                try {
                    await saveSingleQuestionAnswer(testId, [{
                        GroupId: currentQuestion.groupId,
                        AnswerId: noResponse.id,
                        AnswerTitle: noResponse.name,
                        QuestionId: currentQuestion.questionId
                    }]);
                } catch (error) {
                    console.error("Error saving caution question answer:", error);
                }
            }
        }
        dispatch(advanceToNextQuestion());
    };

    return (
        <div className="kt-portlet__body" style={{ height: 'calc(100vh - 65px)' }}>
            <ChatWelcome botName={botSubName} />

            {(phase === 'active' || (phase === 'completed' && showBiometricPrompt)) && !showWelcomePage && (
                <div className="chat-question font-medium">
                    <div className="chat-question__container">
                        <div className="margin-bottom-2-rem">
                            <NewProgressbar progress={progress} />
                        </div>

                        <div className="chat">
                            <div className="chat-info">
                                {(!isExamineticsEnabled && getBodyRegionFromAssessment()) && (
                                    <div className='chat-info-body-region'>
                                        <span className="body-region">{`Body Region: ${getBodyRegionFromAssessment()}`}</span>
                                        <div className="vertical"></div>
                                    </div>
                                )}
                                <div className="chat-info-test-id-and-voice pl-3 border-radius-left-10px">
                                    {(testId || (GLOBAL.ASSESSMENT && GLOBAL.ASSESSMENT['TestId'])) && (
                                        <>
                                            <span className="test-id">
                                                <span className="test-id-text">Test ID: </span>
                                                {testId || GLOBAL.ASSESSMENT['TestId']}
                                            </span>
                                            <div className="vertical"></div>
                                        </>
                                    )}
                                    <div className="voice-toggle">
                                        <p className="voice">EMMA Voice</p>
                                        <div className="button-container">
                                            <VoiceOnOffBtn
                                                isAudioPlayEnabled={isAudioPlayEnabled}
                                                onChangeAudioPlay={onChangeAudioPlay}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="chat-container">
                                <div className="chat-container--details">
                                    <NewQuestionContainer />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {phase === 'completed' && !showWelcomePage && !showBiometricPrompt && (
                (() => {
                    const invitationType = (chatBotNewState.invitationTypeName || '').toLowerCase();
                    const isSelfAssessment = invitationType !== CONSTANT.REDIRECT_TYPE.INCLINIC && invitationType !== CONSTANT.REDIRECT_TYPE.TELEMEDICINE;
                    if (isExamineticsEnabled && isSelfAssessment) return null;
                    return (
                        <div className="chat-question font-medium">
                            <div className="chat-question__container">
                                <div className="chat">
                                    <div className="chat-container">
                                        <div className="chat-container--details">
                                            <NewChatbotCompletedScreen />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()
            )}


            {phase === 'error' && !showWelcomePage && (
                <div className="chat-question font-medium">
                    <div className="chat-question__container">
                        <div className="chat">
                            <div className="chat-container">
                                <div className="chat-container--details">
                                    <div className="mmh-callout">
                                        <div className="mmh-callout__body">
                                            <div className="mmh-callout__content">
                                                <h3 className="mmh-callout__title">Something went wrong</h3>
                                                <div className="mmh-callout__desc">
                                                    <span className="mmh-font-xl">
                                                        Failed to load the assessment. Please close and try again.
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {phase === 'blocked' && (
                <div className="chat-question font-medium">
                    <div className="chat-question__container">
                        <div className="chat">
                            <div className="chat-container">
                                <div className="chat-container--details">
                                    <div className="mmh-callout mmh-callout--danger">
                                        <div className="mmh-callout__body">
                                            <div className="mmh-callout__content">
                                                <h3 className="mmh-callout__title">Pre-Assessment Functional Questionnaire</h3>
                                                <div className="mmh-callout__desc">
                                                    <span className="mmh-font-xl" dangerouslySetInnerHTML={getMarkup(chatBotNewState.exitWorkflowMessage)}>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AlertModal
                show={showBiometricCautionAlert}
                type="warning"
                confirmBtnText="Yes, I want to cancel assessment."
                confirmBtnCssClass="btn btn-danger"
                cancelBtnText="No, I wanted to continue."
                cancelBtnCssClass="btn btn-success"
                showConfirm={true}
                showCancel={true}
                onConfirm={handleClickYesAtPopUpWarning}
                onCancel={handleClickNoAtPopUpWarning}
                onClose={handleClickNoAtPopUpWarning}
                showCloseButton={true}
            >
                {`If you select YES you will not be able to perform your ${isExamineticsEnabled ? 'functional' : 'biometric'} movements. Are you sure?`}
            </AlertModal>
        </div>
    );
};

export default NewChatbotContainer;
